import React, { useState } from "react";
import {
  ImageOff,
  FileText,
  Film,
  Eye,
  X,
  Download,
  ExternalLink,
} from "lucide-react";
import { formatUnderscoreToSpace } from "../../utils/text-format";

// --- Preview Modal Component ---
export const MediaPreviewModal = ({
  isOpen,
  onClose,
  src,
  type = "image",
  alt = "Media Preview",
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[50000] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm transition-opacity">
      <div className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl">
        {/* Modal Header Actions */}
        <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
          {src && (
            <a
              href={src}
              download
              target="_blank"
              rel="noreferrer"
              className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
              title="Download"
            >
              <Download className="h-5 w-5" />
            </a>
          )}
          <button
            onClick={onClose}
            className="rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Content */}
        <div className="flex items-center justify-center p-2">
          {type === "video" ? (
            <video
              src={src}
              controls
              autoPlay
              className="max-h-[80vh] max-w-full rounded-lg"
            >
              Your browser does not support the video tag.
            </video>
          ) : (
            <img
              src={src}
              alt={alt}
              className="max-h-[85vh] max-w-full object-contain rounded-lg"
            />
          )}
        </div>
      </div>
    </div>
  );
};

// --- Single Image View Component ---
export const ImageView = ({ label, value = "" }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <div className="my-4">
      <label className="text-sm font-semibold text-gray-800 capitalize">
        {formatUnderscoreToSpace(label)}
      </label>
      {value ? (
        <div className="group relative h-40 w-40 overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm transition hover:shadow-md">
          <img
            src={value}
            className="h-full w-full object-cover"
            alt={`${label}-preview`}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow hover:bg-white"
            >
              <Eye className="h-4 w-4" /> Preview
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-40 w-40 flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50 text-gray-400">
          <ImageOff className="h-8 w-8 stroke-[1.5]" />
          <span className="mt-1 text-xs">No image</span>
        </div>
      )}

      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        src={value}
        alt={label}
      />
    </div>
  );
};

// --- Document View Component ---
export const DocumentView = ({ label, value = "" }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <div className="my-4">
      <label className="text-sm font-semibold text-gray-800 capitalize">
        {formatUnderscoreToSpace(label)}
      </label>
      {value ? (
        <div className="group relative max-w-[350px] overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-sm transition hover:shadow-md">
          <img
            src={value}
            className="h-48 w-full object-cover"
            alt={`${label}-document`}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow hover:bg-white"
            >
              <Eye className="h-4 w-4" /> View Document
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-48 max-w-[350px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-400">
          <FileText className="h-8 w-8 stroke-[1.5]" />
          <span className="mt-1 text-xs">No document available</span>
        </div>
      )}

      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        src={value}
        alt={label}
      />
    </div>
  );
};

// --- Document Alt View Component ---
export const DocumentAltView = ({ label, value = { url: "", alt: "" } }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <div className="my-4">
      <label className="text-sm font-semibold text-gray-800 capitalize">
        {formatUnderscoreToSpace(label)}
      </label>
      {value?.url ? (
        <div className="group relative max-w-[350px] overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-sm transition hover:shadow-md">
          <img
            src={value.url}
            className="h-48 w-full object-cover"
            alt={value.alt || label}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              onClick={() => setIsPreviewOpen(true)}
              className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow hover:bg-white"
            >
              <Eye className="h-4 w-4" /> View Document
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-48 max-w-[350px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-400">
          <FileText className="h-8 w-8 stroke-[1.5]" />
          <span className="mt-1 text-xs">No document available</span>
        </div>
      )}

      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        src={value?.url}
        alt={value?.alt || label}
      />
    </div>
  );
};

// --- Image Array View Component ---
export const ImageViewArray = ({ label = "Images", value = [] }) => {
  const [activePreview, setActivePreview] = useState(null);

  const formatUnderscoreToSpace = (str) => (str ? str.replace(/_/g, " ") : "");

  // Normalize image list (handles direct string URLs or objects containing a url prop)
  const imageList = Array.isArray(value)
    ? value
        .map((item) =>
          typeof item === "string" ? item : item?.url || item?.path || "",
        )
        .filter(Boolean)
    : [];

  return (
    <div className="w-full my-4">
      {/* Header / Section Title */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <label className="text-sm font-semibold text-gray-800 capitalize">
            {formatUnderscoreToSpace(label)}
          </label>
          {imageList.length > 0 && (
            <p className="text-xs text-gray-500">
              {imageList.length} {imageList.length === 1 ? "image" : "images"}{" "}
              uploaded
            </p>
          )}
        </div>
      </div>

      {/* Main Container */}
      <div className="relative rounded-xl border border-gray-200 bg-gray-50/50 p-4">
        {imageList.length > 0 ? (
          /* Image Grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {imageList.map((url, index) => (
              <div
                key={index}
                className="group relative aspect-square rounded-xl overflow-hidden border border-gray-200 bg-white shadow-xs transition hover:shadow-md"
              >
                <img
                  src={url}
                  alt={`${label}-${index}`}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center p-2">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setActivePreview(url)}
                      className="p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                      title="Preview image"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-full bg-white/20 hover:bg-white/40 text-white transition backdrop-blur-xs"
                      title="Open in new tab"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State Container */
          <div className="flex flex-col items-center justify-center p-8 sm:p-12 text-center rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 text-gray-400">
            <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 mb-2">
              <ImageOff className="w-6 h-6 stroke-[1.5]" />
            </div>
            <p className="text-xs font-medium text-gray-500">
              No images available
            </p>
          </div>
        )}
      </div>

      {/* Image Preview Modal (Lightbox) */}
      {activePreview && (
        <div
          className="fixed inset-0 z-[50001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
          onClick={() => setActivePreview(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActivePreview(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>
            <img
              src={activePreview}
              alt="Preview"
              className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
            />
          </div>
        </div>
      )}
    </div>
  );
};
// --- Video View Component ---
export const VideoView = ({ label, value = "" }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const videoUrl = typeof value === "string" ? value : value?.url;
  const videoName = typeof value === "object" ? value?.name : "";

  return (
    <div className="my-4">
      <p className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400 capitalize">
        {formatUnderscoreToSpace(label)}
      </p>
      {videoUrl ? (
        <div className="max-w-[350px] overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-900 shadow-sm">
          {videoName && (
            <div className="bg-gray-800/80 px-3 py-2 text-xs font-medium text-gray-200 truncate border-b border-gray-700">
              {videoName}
            </div>
          )}
          <div className="group relative h-48 w-full bg-black">
            <video src={videoUrl} className="h-full w-full object-cover" />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity group-hover:bg-black/50">
              <button
                onClick={() => setIsPreviewOpen(true)}
                className="flex items-center gap-1.5 rounded-lg bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-900 shadow hover:bg-white"
              >
                <Film className="h-4 w-4" /> Play Video
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex h-48 max-w-[350px] flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 text-gray-400">
          <Film className="h-8 w-8 stroke-[1.5]" />
          <span className="mt-1 text-xs">No video available</span>
        </div>
      )}

      <MediaPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        src={videoUrl || ""}
        type="video"
        alt={label}
      />
    </div>
  );
};
